import * as Yup from 'yup';
import { Discount } from './DiscountsProviderTypes';
import { addError } from '@shared/utils/logger';

const discountsFor = 'hotels' as const;

const partnerCodeSchema = Yup.object({
  code: Yup.string().required(),
  partnerId: Yup.string().required(),
}).noUnknown();

const baseDiscountSchema = Yup.object({
  code: Yup.string().required(),
  category: Yup.mixed<typeof discountsFor>().oneOf([discountsFor]).required(),
  rule: Yup.string().required(),
  // partnerCodes can be an empty array (no partner restrictions). Don't mark required so empty arrays pass.
  partnerCodes: Yup.array().of(partnerCodeSchema).default([]),
  appliesTo: Yup.string(),
}).noUnknown();

const percentageDiscountSchema = baseDiscountSchema.concat(
  Yup.object({
    type: Yup.mixed<'percentage'>().oneOf(['percentage']).required(),
    percent: Yup.number().required(),
  }).noUnknown(),
);

// Only percentage discounts are supported now, so we can simplify the schema.
const discountsArraySchema: Yup.Schema<ReadonlyArray<Discount>> = Yup.array()
  .of(percentageDiscountSchema as Yup.Schema<Discount>)
  .required()
  .strict(true);

/**
 * Validates the payload from amplitude containing coupon codes.
 * @param payload -- Payload from amplitude containing coupon codes
 * @returns True if the payload is valid, false otherwise.
 */
export const validatePayload = (payload: unknown): payload is ReadonlyArray<Discount> => {
  try {
    discountsArraySchema.validateSync(payload, { strict: true });
    return true;
  } catch (error) {
    addError('validateDiscountsPayload', { error });
    return false;
  }
};

export const findCouponForKeyValue = (key: keyof Discount, value: string | number, payload: unknown) => {
  if (!validatePayload(payload)) {
    return undefined;
  }
  return payload.find(item => item[key] === value);
};

export const applyDiscount = (valueInMinorUnits: number, discount?: Readonly<Discount>): number => {
  if (!discount) return valueInMinorUnits;
  if (discount.type === 'percentage') {
    if (discount.percent <= 0) return valueInMinorUnits;
    if (discount.percent >= 100) return 0; // 100% (or greater) discount results in free.
    return Math.round(valueInMinorUnits * (1 - discount.percent / 100));
  }
  return valueInMinorUnits; // Fallback to no discount applied
};
